import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import { AdminSidebar, getAdminSectionTitle } from '@/components/AdminSidebar';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Globe, LogOut, User } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuthUser } from '@/hooks/useAuthUser';

export function AdminLayout() {
  const user = useAuthUser();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  const sectionTitle = getAdminSectionTitle(location.pathname);
  const email = user?.email ?? '';

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full bg-background antialiased">
        <AdminSidebar />

        <div className="flex min-w-0 flex-1 flex-col">
          <header className="sticky top-0 z-30 flex h-16 items-center gap-2 bg-background/85 px-3 backdrop-blur-md md:px-6 surface-raised">
            <SidebarTrigger className="h-10 w-10 shrink-0 rounded-xl transition-[colors,transform] duration-200 active:scale-[0.96]" />

            <div className="min-w-0 flex-1">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground">
                Amministrazione
              </p>
              <h1 className="truncate font-serif text-base font-semibold leading-tight md:text-lg">
                {sectionTitle}
              </h1>
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="h-10 gap-2 rounded-xl px-2 transition-[colors,transform] duration-200 active:scale-[0.96] md:px-3"
                >
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-secondary-container text-on-secondary-container">
                    <User className="h-4 w-4" />
                  </span>
                  <span className="hidden max-w-[180px] truncate text-sm md:inline">{email}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 rounded-xl">
                <DropdownMenuLabel className="truncate text-xs font-normal text-muted-foreground">
                  {email || 'Amministratore'}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="rounded-lg"
                  onClick={() => window.open('/', '_blank', 'noreferrer')}
                >
                  <Globe className="mr-2 h-4 w-4" />
                  Visualizza Sito
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="rounded-lg text-destructive focus:text-destructive"
                  onClick={handleLogout}
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Esci
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </header>

          <main className="min-w-0 flex-1 px-4 py-6 md:px-6 lg:px-8">
            <div className="mx-auto w-full max-w-screen-2xl">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}
